import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, CheckCircle2, PackageCheck, Printer, Send } from 'lucide-react';
import { PO_STATUS, dateLabel } from '@adisys/shared';
import { api, ApiRequestError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Fact, MetaBadge, Notice, errorText, inr2, qtyLabel } from '../../lib/crm';
import { PageHeader } from '../../components/AppShell';
import { Button, Card, CardHeader, ErrorState, Field, Input, Modal, Skeleton, Textarea, cx, useToast } from '../../components/ui';

const STEPS = [
  { key: 'draft', label: 'Drafted' }, { key: 'issued', label: 'Issued to vendor' },
  { key: 'confirmed', label: 'Vendor confirmed' }, { key: 'received', label: 'Received' },
] as const;
const rank = (s: string) => ({ draft: 0, issued: 1, confirmed: 2, partially_received: 2, received: 3, cancelled: -1 } as Record<string, number>)[s] ?? 0;

export function PurchaseOrderDetailPage() {
  const { id } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['crm', 'po', id], queryFn: () => api.get(`/crm/procurement/purchase-orders/${id}`) });
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['crm'] }); };
  const manage = can('crm.procurement.manage');

  const [modal, setModal] = useState<'confirm' | 'receive' | 'cancel' | null>(null);
  const [error, setError] = useState<unknown>(null);
  const fe = (k: string) => (error instanceof ApiRequestError ? error.fieldError(k) : undefined);
  const [conf, setConf] = useState({ expectedDelivery: '', vendorReference: '' });
  const [recv, setRecv] = useState<Record<string, string>>({});
  const [rf, setRf] = useState({ receivedOn: '', vendorChallanNo: '', note: '' });
  const [reason, setReason] = useState('');
  const [result, setResult] = useState<any>(null);

  const po = q.data;
  useEffect(() => { if (po && modal === 'confirm') setConf({ expectedDelivery: po.expectedDelivery ?? '', vendorReference: po.vendorReference ?? '' }); }, [modal, po]);

  const issue = useMutation({
    mutationFn: () => api.post(`/crm/procurement/purchase-orders/${id}/issue`),
    onSuccess: () => { toast.success('Purchase order issued'); refresh(); },
    onError: (e) => toast.error('Could not issue', errorText(e)),
  });
  const confirm = useMutation({
    mutationFn: () => api.post(`/crm/procurement/purchase-orders/${id}/confirm`, { expectedDelivery: conf.expectedDelivery, vendorReference: conf.vendorReference || undefined }),
    onSuccess: () => { toast.success('Vendor confirmation recorded'); setModal(null); refresh(); },
    onError: (e) => setError(e),
  });
  const receive = useMutation({
    mutationFn: () => api.post(`/crm/procurement/purchase-orders/${id}/receive`, {
      receivedOn: rf.receivedOn || undefined, vendorChallanNo: rf.vendorChallanNo || undefined, note: rf.note || undefined,
      items: Object.entries(recv).filter(([, v]) => Number(v) > 0).map(([poItemId, v]) => ({ poItemId, quantity: Number(v) })),
    }),
    onSuccess: (r: any) => { setResult(r); setModal(null); refresh(); },
    onError: (e) => setError(e),
  });
  const cancel = useMutation({
    mutationFn: () => api.post(`/crm/procurement/purchase-orders/${id}/cancel`, { reason }),
    onSuccess: () => { toast.success('Purchase order cancelled'); setModal(null); refresh(); },
    onError: (e) => setError(e),
  });

  if (q.isLoading) return <div className="space-y-4 p-6"><Skeleton className="h-10 w-72" /><Skeleton className="h-64 w-full" /></div>;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const items: any[] = po.items;
  const step = rank(po.status);
  const receivable = ['issued', 'confirmed', 'partially_received'].includes(po.status);

  return (
    <>
      <PageHeader title={po.poNo} description={`${po.vendorName}${po.projectName ? ` · ${po.projectName}` : ' · Stock replenishment'}`}
        actions={
          <>
            <Button icon={<Printer className="h-4 w-4" />} onClick={() => window.print()}>Print</Button>
            {manage && po.status === 'draft' && <Button variant="primary" icon={<Send className="h-4 w-4" />} loading={issue.isPending} onClick={() => issue.mutate()}>Issue to vendor</Button>}
            {manage && po.status === 'issued' && <Button variant="primary" icon={<CheckCircle2 className="h-4 w-4" />} onClick={() => { setError(null); setModal('confirm'); }}>Vendor confirmed</Button>}
            {manage && receivable && <Button variant="primary" icon={<PackageCheck className="h-4 w-4" />} onClick={() => { setError(null); setRecv({}); setModal('receive'); }}>Receive goods</Button>}
            {manage && ['draft', 'issued', 'confirmed'].includes(po.status) && <Button variant="danger" icon={<Ban className="h-4 w-4" />} onClick={() => { setError(null); setReason(''); setModal('cancel'); }}>Cancel</Button>}
          </>
        }>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <MetaBadge meta={PO_STATUS} value={po.status} />
          {po.projectId && <Link className="text-brand-600 hover:underline" to={`/crm/projects/${po.projectId}?tab=procurement`}>{po.projectCode}</Link>}
          {po.isLate && <span className="font-medium text-danger">Delivery is late</span>}
        </div>
      </PageHeader>

      <div className="space-y-5 p-4 sm:p-6">
        {result && (
          <Notice tone="success">Goods received on {result.grnNo}; purchase order is now {String(result.poStatus).replace('_', ' ')}.
            {Number(result.reservedForCustomers) > 0 && ` ${qtyLabel(result.reservedForCustomers)} units were reserved for the customer order.`}
            {result.projectsAdvanced > 0 && ' The project has everything it needs and moved on to invoicing and delivery.'}</Notice>)}

        {po.status !== 'cancelled' ? (
          <ol className="no-print grid gap-2 sm:grid-cols-4">
            {STEPS.map((s, n) => (
              <li key={s.key} className={cx('rounded-lg px-3 py-2 text-xs font-medium ring-1 ring-inset',
                n <= step ? 'bg-success-soft text-success ring-success/20' : 'bg-ink-50 text-ink-500 ring-line')}>
                {n + 1}. {s.label}{po.status === 'partially_received' && s.key === 'received' ? ' (partly)' : ''}</li>))}
          </ol>) : <Notice tone="warning">This purchase order was cancelled.{po.note ? ` ${po.note}` : ''}</Notice>}

        <div className="grid gap-5 xl:grid-cols-3">
          <Card className="xl:col-span-2" padded={false}>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[620px] text-left text-sm">
                <thead className="bg-sunken/60 text-xs text-ink-600"><tr><th className="px-3 py-2.5">Product</th>
                  <th className="px-3 py-2.5 text-right">Ordered</th><th className="px-3 py-2.5 text-right">Received</th>
                  <th className="px-3 py-2.5 text-right">Outstanding</th><th className="px-3 py-2.5 text-right">Unit cost</th><th className="px-3 py-2.5 text-right">GST</th></tr></thead>
                <tbody className="divide-y divide-line">
                  {items.map((i) => (
                    <tr key={i.id}>
                      <td className="px-3 py-2.5"><p className="font-medium text-ink-900">{i.name}</p><p className="text-xs text-ink-500">{i.sku}{i.modelNumber ? ` · ${i.modelNumber}` : ''}</p></td>
                      <td className="tabular px-3 py-2.5 text-right">{qtyLabel(i.quantity)} {i.unit}</td>
                      <td className="tabular px-3 py-2.5 text-right">{qtyLabel(i.receivedQty)}</td>
                      <td className={cx('tabular px-3 py-2.5 text-right', Number(i.outstanding) > 0 && 'font-medium text-ink-900')}>{qtyLabel(i.outstanding)}</td>
                      <td className="tabular px-3 py-2.5 text-right">{inr2(i.unitCost)}</td>
                      <td className="tabular px-3 py-2.5 text-right">{qtyLabel(i.gstRate)}%</td>
                    </tr>))}
                </tbody>
              </table>
            </div>
            <dl className="tabular ml-auto w-full max-w-xs space-y-1 border-t border-line p-4 text-sm">
              <div className="flex justify-between"><dt className="text-ink-500">Subtotal</dt><dd>{inr2(po.subtotal)}</dd></div>
              <div className="flex justify-between"><dt className="text-ink-500">GST</dt><dd>{inr2(po.gstTotal)}</dd></div>
              <div className="flex justify-between border-t border-line pt-1 font-semibold text-ink-900"><dt>Total</dt><dd>{inr2(po.grandTotal)}</dd></div>
            </dl>
          </Card>

          <div className="space-y-5">
            <Card>
              <dl className="grid gap-4">
                <Fact label="Vendor">{po.vendorName}{po.vendorContact && <span className="block text-xs text-ink-500">{po.vendorContact}{po.vendorPhone ? ` · ${po.vendorPhone}` : ''}</span>}</Fact>
                <Fact label="Expected delivery">{po.expectedDelivery ? dateLabel(po.expectedDelivery) : ''}</Fact>
                <Fact label="Vendor reference">{po.vendorReference}</Fact>
                <Fact label="Payment terms">{po.paymentTerms}</Fact>
                <Fact label="Raised by">{po.createdByName} on {dateLabel(po.createdAt)}</Fact>
              </dl>
            </Card>
            <Card>
              <CardHeader title="Goods received" />
              {po.receipts.length === 0 ? <p className="text-sm text-ink-500">Nothing received yet.</p> : (
                <ul className="divide-y divide-line">
                  {po.receipts.map((r: any) => (
                    <li key={r.id} className="py-2.5 text-sm"><p className="font-medium text-ink-900">{r.grnNo} · {qtyLabel(r.units)} units</p>
                      <p className="text-xs text-ink-500">{dateLabel(r.receivedOn)} · {r.receivedByName}{r.vendorChallanNo ? ` · vendor challan ${r.vendorChallanNo}` : ''}</p>
                      {r.note && <p className="text-xs text-ink-500">{r.note}</p>}</li>))}
                </ul>)}
            </Card>
          </div>
        </div>
      </div>

      <Modal open={modal === 'confirm'} onClose={() => setModal(null)} size="sm" title="Vendor confirmation"
        footer={<><Button onClick={() => setModal(null)}>Cancel</Button>
          <Button variant="primary" loading={confirm.isPending} disabled={!conf.expectedDelivery} onClick={() => confirm.mutate()}>Save</Button></>}>
        <div className="space-y-3">
          {error instanceof ApiRequestError && !fe('expectedDelivery') && <Notice tone="danger">{error.message}</Notice>}
          <Field label="Delivery date promised by the vendor" required error={fe('expectedDelivery')}>
            <Input type="date" value={conf.expectedDelivery} onChange={(e) => setConf({ ...conf, expectedDelivery: e.target.value })} /></Field>
          <Field label="Vendor's order / reference no."><Input value={conf.vendorReference} onChange={(e) => setConf({ ...conf, vendorReference: e.target.value })} /></Field>
        </div>
      </Modal>

      <Modal open={modal === 'receive'} onClose={() => setModal(null)} size="lg" title="Receive goods"
        description="Enter what actually arrived. Anything bought for a customer order is reserved for it automatically."
        footer={<><Button onClick={() => setModal(null)}>Cancel</Button>
          <Button variant="primary" loading={receive.isPending} disabled={!Object.values(recv).some((v) => Number(v) > 0)} onClick={() => receive.mutate()}>Record receipt</Button></>}>
        <div className="space-y-4">
          {error instanceof ApiRequestError && <Notice tone="danger">{error.message}</Notice>}
          <div className="overflow-x-auto rounded-lg ring-1 ring-line">
            <table className="w-full min-w-[480px] text-left text-sm">
              <thead className="bg-sunken/60 text-xs text-ink-600"><tr><th className="px-3 py-2">Product</th>
                <th className="px-3 py-2 text-right">Outstanding</th><th className="w-28 px-3 py-2 text-right">Received now</th></tr></thead>
              <tbody className="divide-y divide-line">
                {items.map((i) => (
                  <tr key={i.id}><td className="px-3 py-2 font-medium text-ink-900">{i.name}</td>
                    <td className="tabular px-3 py-2 text-right">{qtyLabel(i.outstanding)}</td>
                    <td className="px-3 py-2"><Input type="number" min={0} max={Number(i.outstanding)} step="any" className="text-right" disabled={Number(i.outstanding) <= 0}
                      aria-label={`Received quantity of ${i.name}`} value={recv[i.id] ?? ''} onChange={(e) => setRecv({ ...recv, [i.id]: e.target.value })} /></td></tr>))}
              </tbody>
            </table>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Received on" error={fe('receivedOn')}><Input type="date" value={rf.receivedOn} onChange={(e) => setRf({ ...rf, receivedOn: e.target.value })} /></Field>
            <Field label="Vendor challan no."><Input value={rf.vendorChallanNo} onChange={(e) => setRf({ ...rf, vendorChallanNo: e.target.value })} /></Field>
          </div>
          <Field label="Note"><Textarea rows={2} value={rf.note} onChange={(e) => setRf({ ...rf, note: e.target.value })} /></Field>
        </div>
      </Modal>

      <Modal open={modal === 'cancel'} onClose={() => setModal(null)} size="sm" title="Cancel purchase order"
        footer={<><Button onClick={() => setModal(null)}>Keep order</Button>
          <Button variant="danger" loading={cancel.isPending} disabled={reason.trim().length < 3} onClick={() => cancel.mutate()}>Cancel order</Button></>}>
        <div className="space-y-3">
          {error instanceof ApiRequestError && !fe('reason') && <Notice tone="danger">{error.message}</Notice>}
          <Field label="Reason" required error={fe('reason')}><Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
        </div>
      </Modal>
    </>
  );
}
